<template>
  <BaseDialog
    ref="dialog"
    contentClass="tender-kw-drawer"
    :show-actions="true"
    :on-close="closeModal"
    @closeModal="closeModal"
  >
    <template slot="title">{{ $t('menu.keywords') }}</template>
    <template slot="content">
      <div class="kwdrawer">
        <BaseInfoBanner
          v-if="!hasKeywordsSelected"
          class="kwdrawer__banner"
          tone="info"
          preset="soft"
          :title="$t('dialog.keywords.no-filter-title')"
          :description="$t('dialog.keywords.no-filter-description')"
        >
          <template #icon>
            <BaseIcon name="info" />
          </template>
        </BaseInfoBanner>
        <!-- Match all keywords -->
        <div class="kwdrawer__allkw">
          <div class="kwdrawer__allkw-text">
            <div class="kwdrawer__allkw-title">{{ $t('dialog.filters.all-keywords') }}</div>
            <div class="kwdrawer__allkw-desc">{{ $t('dialog.filters.all-keywords-desc') }}</div>
          </div>
          <BaseToggle :value="allKeywords" is-small @change="allKeywords = $event" />
        </div>

        <!-- Dimmed while "all keywords" is on; overlay click turns that toggle off so the
             section becomes interactive without the user having to hunt for the toggle. -->
        <div class="kwdrawer__body-wrap">
          <div class="kwdrawer__body" :class="{ kwdrawer__disabled: allKeywords }">
            <BaseLoadingBar is-small v-if="pending" />
            <template v-else-if="languages.length">
              <!-- Language is a BROWSE filter (which categories are listed), not a selection —
                   pills instead of checkboxes so it can't be read as "these languages are selected".
                   Hidden when the account only has one language (nothing to filter). -->
              <div class="kwdrawer__langbar">
                <BasePillGroup
                  v-if="availableLanguages.length > 1"
                  is-small
                  :options="langOptions"
                  :value="langFilter"
                  @input="langFilter = $event"
                />
                <!-- Live selection status — makes "no keywords" explicit (e.g. after "Effacer tout").
                     When "all keywords" is on, show the full tenant count so it matches the checked boxes. -->
                <span class="kwdrawer__selcount">
                  {{ $tc('dialog.keywords.selected-count', displaySelectedCount, { count: displaySelectedCount }) }}
                </span>
              </div>

              <!-- categories (left) + keywords (right); on phones, one column, a category's keywords
                   under it once it's opened -->
              <div class="kwdrawer__grid">
                <div class="kwdrawer__cats">
                  <template v-for="cat in visibleCategories">
                    <div
                      :key="cat.id"
                      class="kwdrawer__cat"
                      :class="{ 'is-sel': !allKeywords && cat.id === activeCategoryId }"
                      @click="onCategoryClick(cat)"
                    >
                      <!-- Checkbox toggles selecting the whole category; stop propagation so ticking it
                         doesn't also switch the viewed category (that's the rest-of-row's job). -->
                      <FormCheckbox
                        size="small"
                        :value="catState(cat) === 'on'"
                        :indeterminate="catState(cat) === 'mixed'"
                        :after-indeterminate="true"
                        @input="toggleCategory(cat)"
                        @click.native.stop
                      />
                      <div class="kwdrawer__cat-text">
                        <div class="kwdrawer__cat-name">{{ cat.name }}</div>
                        <div class="kwdrawer__cat-count">
                          {{
                            $tc('dialog.keywords.keywords.count', cat.keywords.length, { count: cat.keywords.length })
                          }}
                        </div>
                      </div>
                      <!-- Language tag only disambiguates when several languages are listed together. -->
                      <span v-if="langFilter === null && availableLanguages.length > 1" class="kwdrawer__langtag">
                        {{ $t(`languages.${cat.language}`) }}
                      </span>
                      <BaseIcon
                        v-if="isPhone"
                        :name="cat.id === activeCategoryId ? 'chevron-up' : 'chevron-down'"
                        class="kwdrawer__cat-car"
                      />
                    </div>
                    <TenderKeywordChecklist
                      v-if="isPhone && cat.id === activeCategoryId"
                      :key="`${cat.id}-words`"
                      class="kwdrawer__cat-words"
                      :keywords="activeCategoryKeywords"
                      :is-selected="isSelected"
                      @toggle="toggleWord"
                    />
                  </template>
                </div>

                <div v-if="!isPhone" class="kwdrawer__words">
                  <TenderKeywordChecklist
                    v-if="activeCategoryId"
                    :keywords="activeCategoryKeywords"
                    :is-selected="isSelected"
                    @toggle="toggleWord"
                  />
                  <!-- No category picked yet — say so instead of showing a silent blank column. -->
                  <div v-else class="kwdrawer__words-empty">{{ $t('dialog.keywords.select-category') }}</div>
                </div>
              </div>
            </template>
            <div v-else class="kwdrawer__empty">{{ $t('page.keywords.no-keywords') }}</div>
          </div>
          <div v-if="allKeywords" class="kwdrawer__unlock" @click="allKeywords = false" />
        </div>

        <!-- Match context sits OUTSIDE __body so title/description stay editable even when "all
             keywords" is on — only the individual keyword selection above is dimmed.
             Disabled when no keywords are in play (nothing to match against). -->
        <div class="kwdrawer__match" :class="{ kwdrawer__disabled: !hasKeywordsSelected }">
          <div class="kwdrawer__match-title">{{ $t('search-filters.match-context.name') }}</div>
          <div class="kwdrawer__match-desc">{{ $t('search-filters.match-context.subtitle') }}</div>
          <div class="kwdrawer__match-opts">
            <FormCheckbox
              v-for="ctx in matchContextValues"
              :key="ctx"
              size="small"
              :disabled="!hasKeywordsSelected"
              :value="matchContexts.includes(ctx)"
              :label="$t(`search-filters.match-context.values.${ctx}`)"
              @input="toggleMatch(ctx)"
            />
          </div>
        </div>
      </div>
    </template>
    <template slot="actions">
      <div class="kwdrawer__foot">
        <BaseButton isTransparent isUnderline isSquare @click.native="clearAll">
          {{ $t('dialog.filters.advanced.clear-all') }}
        </BaseButton>
        <span class="kwdrawer__foot-spacer"></span>
        <BaseButton :disabled="!isDirty" @click.native="apply">{{
          $t('dialog.filters.advanced.apply-all')
        }}</BaseButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import BaseInfoBanner from '@/components/base/BaseInfoBanner';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BasePillGroup from '@/components/base/BasePillGroup';
import BaseToggle from '@/components/base/BaseToggle';
import FormCheckbox from '@/components/form/FormCheckbox';
import { MATCH_CONTEXT_VALUES } from '@/models/search-filters';
import { viewport } from '@/util/viewport';
import TenderKeywordChecklist from '@/components/page-tenders/views/TenderKeywordChecklist.vue';

export default {
  name: 'TenderKeywordsDrawer',
  components: {
    BaseButton,
    BaseDialog,
    BaseIcon,
    BaseInfoBanner,
    BaseLoadingBar,
    BasePillGroup,
    BaseToggle,
    FormCheckbox,
    TenderKeywordChecklist,
  },
  props: {
    searchFilters: { type: Object, required: true },
  },
  data() {
    return {
      matchContextValues: MATCH_CONTEXT_VALUES,
      activeCategoryId: null,
      // Browse filter for the category list: null = all languages, or a single language code.
      langFilter: null,
      selected: [],
      matchContexts: [],
      allKeywords: false,
      // Snapshot of the selection the drawer opened with — the baseline for the Apply dirty state.
      initial: { selected: [], matchContexts: [], allKeywords: false },
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('keywords', ['languages', 'pending']),
    availableLanguages() {
      return this.languages.map((l) => l.language);
    },
    // Pill options for the language browse filter: "Voir tout" (null) + one pill per language.
    langOptions() {
      return [
        { value: null, label: this.$t('dialog.keywords.all-languages') },
        ...this.availableLanguages.map((lang) => ({ value: lang, label: this.$t(`languages.${lang}`) })),
      ];
    },
    visibleCategories() {
      const cats = [];
      this.languages.forEach((l) => {
        if (this.langFilter === null || l.language === this.langFilter) l.categories.forEach((c) => cats.push(c));
      });
      return cats;
    },
    activeCategoryKeywords() {
      const cat = this.visibleCategories.find((c) => c.id === this.activeCategoryId);
      return cat ? cat.keywords : [];
    },
    // All keyword ids across languages — used to mirror "all keywords" in the UI.
    allKeywordIds() {
      const ids = [];
      this.languages.forEach((l) => {
        l.categories.forEach((c) => c.keywords.forEach((k) => ids.push(k.id)));
      });
      return ids;
    },
    // Status label count: full tenant set when "all keywords" is on, else the working selection.
    displaySelectedCount() {
      return this.allKeywords ? this.allKeywordIds.length : this.selected.length;
    },
    // Keywords are in play when at least one is selected, or "all keywords" is on.
    hasKeywordsSelected() {
      return this.allKeywords || this.selected.length > 0;
    },
    // Apply is enabled only when the working selection differs from what the drawer opened with
    // (order-independent), so re-applying an unchanged selection isn't offered.
    isDirty() {
      const sameSet = (a, b) => a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');
      return (
        this.allKeywords !== this.initial.allKeywords ||
        !sameSet(this.selected, this.initial.selected) ||
        !sameSet(this.matchContexts, this.initial.matchContexts)
      );
    },
  },
  watch: {
    // If the selected category disappears (e.g. its language is toggled off), clear the selection
    // rather than jumping to another one — and never auto-select on initial load.
    visibleCategories(cats) {
      if (this.activeCategoryId && !cats.find((c) => c.id === this.activeCategoryId)) {
        this.activeCategoryId = null;
      }
    },
  },
  methods: {
    ...mapActions('keywords', ['getKeywords']),
    // Phones open a category under itself, and close it with a second tap.
    onCategoryClick(cat) {
      this.activeCategoryId = this.isPhone && this.activeCategoryId === cat.id ? null : cat.id;
    },
    init() {
      if (!this.languages.length) this.getKeywords();
      this.selected = [...(this.searchFilters.keywordIds || [])];
      this.matchContexts = [...(this.searchFilters.matchContexts || [])];
      this.allKeywords = !!this.searchFilters.allKeywords;
      // Browse all languages by default; no category selected — the user picks one.
      this.langFilter = null;
      this.initial = {
        selected: [...this.selected],
        matchContexts: [...this.matchContexts],
        allKeywords: this.allKeywords,
      };
    },
    isSelected(id) {
      // Visually select every keyword while "all keywords" is on (selection state stays untouched).
      return this.allKeywords || this.selected.includes(id);
    },
    toggleWord(id) {
      if (this.selected.includes(id)) this.selected = this.selected.filter((x) => x !== id);
      else this.selected = [...this.selected, id];
    },
    catState(cat) {
      if (this.allKeywords) return 'on';
      const on = cat.keywords.filter((k) => this.selected.includes(k.id)).length;
      if (on === 0) return 'off';
      if (on === cat.keywords.length) return 'on';
      return 'mixed';
    },
    toggleCategory(cat) {
      const ids = cat.keywords.map((k) => k.id);
      if (this.catState(cat) === 'on') {
        this.selected = this.selected.filter((x) => !ids.includes(x));
      } else {
        const set = new Set(this.selected);
        ids.forEach((id) => set.add(id));
        this.selected = [...set];
      }
    },
    toggleMatch(ctx) {
      if (this.matchContexts.includes(ctx)) this.matchContexts = this.matchContexts.filter((x) => x !== ctx);
      else this.matchContexts = [...this.matchContexts, ctx];
    },
    clearAll() {
      this.selected = [];
      this.matchContexts = [];
      this.allKeywords = false;
    },
    apply() {
      this.$emit('apply', {
        keywordIds: this.selected,
        matchContexts: this.matchContexts,
        allKeywords: this.allKeywords,
      });
    },
    closeModal() {
      this.$emit('close-dialog');
    },
  },
  created() {
    this.init();
    this.$nextTick(() => {
      this.$refs.dialog.isModalOpen = true;
    });
  },
};
</script>

<style lang="scss">
// Right-side drawer (BaseDialog default), widened to 580px.
.base-dialog__tender-kw-drawer {
  width: 680px;
  max-width: 100%;

  .base-dialog__header {
    border-bottom: 1px solid $color-border;
  }

  // Drawer body surface is pure white (overrides BaseDialog's neutral-pale
  // header+content default). overflow:hidden pins the drawer to the viewport height —
  // the category/keyword columns scroll individually instead of the whole content.
  .base-dialog__header + .base-dialog__content {
    background-color: $color-white;
    padding: 20px 22px;
    overflow: hidden;
  }

  .base-dialog__actions {
    padding: 16px 22px;
    border-top: 1px solid $color-border;
  }

  // Phones: the panel scrolls as a whole, its categories in one column.
  @include screen-below('tablet') {
    .base-dialog__header + .base-dialog__content {
      padding: $space-md;
      overflow-y: auto;
    }

    .base-dialog__actions {
      padding: $space-3 $space-md;
    }
  }
}

.kwdrawer {
  // Fill the dialog content and delegate scrolling to the grid columns (fixed drawer height).
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  // ~18px checks (DS "small" is 16px).
  .form-checkbox--small .form-checkbox__check {
    width: 18px;
    height: 18px;
  }

  &__allkw {
    display: flex;
    align-items: center;
    gap: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid $color-border;
  }

  &__banner {
    margin-bottom: 16px;
    flex-shrink: 0;
  }

  &__allkw-title {
    font-weight: 800;
    color: $color-normal;
    font-size: 14px;
  }

  &__allkw-desc {
    font-size: 12px;
    color: $color-slate-500;
    margin-top: 2px;
  }

  // Wraps the keyword body so an unlock overlay can sit on top while "all keywords" is on.
  &__body-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  // Everything under the all-keywords toggle; flexes so the grid (not the drawer) scrolls.
  &__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  // Click catcher over the dimmed body — turns off "all keywords" so the section unlocks.
  &__unlock {
    position: absolute;
    inset: 0;
    z-index: 1;
    cursor: pointer;
  }

  &__disabled {
    opacity: 0.45;
    pointer-events: none;
  }

  &__langbar {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 8px 0;
    border-bottom: 1px solid $color-border;
  }

  // Selection status ("Aucun mot-clé sélectionné" / "N mots-clés sélectionnés").
  &__selcount {
    margin-left: auto;
    font-size: 12px;
    font-style: italic;
    color: $color-slate-500;
  }

  &__grid {
    display: grid;
    grid-template-columns: 240px 1fr;
    // Single row clamped to the grid's height so each column scrolls on its own.
    grid-template-rows: minmax(0, 1fr);
    gap: 8px 22px;
    padding: 16px 0;
    flex: 1;
    min-height: 0;
  }

  &__cats {
    overflow-y: auto;
  }

  &__cat {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: $radius-md;
    cursor: pointer;

    &:hover {
      background: $color-slate-50;
    }

    &.is-sel {
      background: rgba($color-accent-500, 0.1);

      .kwdrawer__cat-name {
        color: $color-accent-600;
      }
    }
  }

  &__cat-text {
    min-width: 0;
    cursor: pointer;
  }

  &__cat-name {
    font-size: 13px;
    font-weight: 800;
    color: $color-normal;
    line-height: 1.3;
  }

  &__cat-count {
    font-size: 11px;
    color: $color-slate-500;
    font-style: italic;
  }

  &__langtag {
    margin-left: auto;
    font-size: 10px;
    font-weight: 800;
    color: $color-slate-600;
    background: $color-hover;
    border-radius: $radius-full;
    padding: 2px 8px;
  }

  &__words {
    display: flex;
    flex-direction: column;
    padding-top: 4px;
    overflow-y: auto;
  }

  &__kw-value {
    margin-left: 16px;
  }

  &__words-empty {
    padding: 8px;
    font-size: 12px;
    font-style: italic;
    color: $color-slate-400;
  }

  &__empty {
    padding: 24px 0;
    font-size: 14px;
    color: $color-slate-400;
  }

  &__match {
    padding-top: 16px;
    border-top: 1px solid $color-border;
  }

  &__match-title {
    font-size: 15px;
    font-weight: 800;
    color: $color-normal;
  }

  &__match-desc {
    font-size: 13px;
    color: $color-slate-400;
    margin-top: 4px;
  }

  &__match-opts {
    display: flex;
    gap: 16px;
    margin-top: 8px;
  }
}

// Phones: one column that scrolls with the panel; a category opens its keywords under it.
.kwdrawer {
  @include screen-below('tablet') {
    &,
    &__body-wrap,
    &__body {
      flex: none;
    }

    &__langbar {
      flex-wrap: wrap;
      gap: $space-2;
    }

    &__selcount {
      flex-basis: 100%;
      margin-left: 0;
    }

    &__grid {
      display: block;
      padding: $space-2 0;
    }

    &__cats {
      overflow: visible;
    }

    &__cat {
      gap: $space-2;
      min-height: 48px;
    }

    &__cat-text {
      flex: 1;
    }

    &__cat-car {
      flex: none;
      width: 18px;
      height: 18px;
      color: $color-slate-500;
    }

    &__langtag {
      margin-left: 0;
    }

    &__cat-words {
      padding: 0 0 $space-2 36px;

      .form-checkbox {
        min-height: 44px;
      }
    }

    &__match-opts {
      flex-wrap: wrap;
    }
  }
}

// Footer lives in BaseDialog's actions slot.
.kwdrawer__foot {
  display: flex;
  align-items: center;
  width: 100%;

  &-spacer {
    margin-left: auto;
  }
}
</style>
